import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../hooks/useTheme';
import { readCssVar, usePrefersReducedMotion } from '../measurements/historyChartUtils';
import { BUDGET_CATEGORIES, type BudgetCategory, type BudgetSummary } from '../../services/budgetService';
import { categoryColourVar, cumulativeSeries, monthLabel, monthsElapsed, rollingAverage } from './budgetFormat';
import { useMoney } from './useMoney';
import './BudgetCharts.css';

interface BaseColours {
  grid: string;
  axis: string;
  current: string;
  previous: string;
  forecast: string;
}

/** Token colours for the charts — re-read when the theme changes. */
function useBudgetColours(): { base: BaseColours; category: Record<BudgetCategory, string> } {
  const { theme } = useTheme();
  const read = () => ({
    base: {
      grid: readCssVar('--color-border-secondary'),
      axis: readCssVar('--color-text-muted'),
      current: readCssVar('--color-primary'),
      previous: readCssVar('--color-text-muted'),
      forecast: readCssVar('--color-status-info'),
    },
    category: Object.fromEntries(BUDGET_CATEGORIES.map((c) => [c, readCssVar(categoryColourVar(c))])) as Record<BudgetCategory, string>,
  });
  const [colours, setColours] = useState(read);
  useEffect(() => {
    setColours(read());
  }, [theme]);
  return colours;
}

function ChartFrame({ caption, testId, height = 300, children }: { caption: ReactNode; testId: string; height?: number; children: ReactElement }) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <figure className="budget-chart">
      <figcaption className="budget-chart__caption">{caption}</figcaption>
      <div className="budget-chart__plot" aria-hidden="true" data-testid={testId} data-animated={reducedMotion ? 'false' : 'true'}>
        <ResponsiveContainer width="100%" height={height}>
          {children}
        </ResponsiveContainer>
      </div>
    </figure>
  );
}

function useAxis() {
  const money = useMoney();
  const { base } = useBudgetColours();
  return {
    language: money.language,
    tick: { fill: base.axis, fontSize: 12 },
    money: (value: number) => money.format(Math.round(value * 100), { compact: true }),
    tooltipMoney: (value: unknown) => (typeof value === 'number' ? money.format(Math.round(value * 100)) : '–'),
  };
}

export interface BudgetChartProps {
  summary: BudgetSummary;
  caption: ReactNode;
}

/** Monthly spend of the year, stacked by category; for the current year the forecast of the remaining months as a translucent bar. */
export function MonthlyCategoryChart({ summary, caption, categories }: BudgetChartProps & { categories: readonly BudgetCategory[] }) {
  const { t } = useTranslation();
  const { base, category: colours } = useBudgetColours();
  const { language, tick, money, tooltipMoney } = useAxis();
  const reducedMotion = usePrefersReducedMotion();
  const data = useMemo(
    () =>
      summary.months.map((m) => {
        const row: Record<string, number | string> = { label: monthLabel(m.month, language) };
        for (const c of categories) row[c] = m.byCategory[c] / 100;
        row.forecast = m.projectedCents !== null ? Math.max(0, m.projectedCents - m.totalCents) / 100 : 0;
        return row;
      }),
    [summary, categories, language],
  );
  return (
    <ChartFrame caption={caption} testId="budget-monthly-chart">
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={base.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" stroke={base.axis} tick={tick} tickLine={false} interval="preserveStartEnd" minTickGap={4} />
        <YAxis stroke={base.axis} tick={tick} tickLine={false} tickFormatter={money} width={64} />
        <Tooltip wrapperClassName="budget-chart__tooltip" formatter={tooltipMoney} isAnimationActive={!reducedMotion} />
        <Legend />
        {categories.map((c) => (
          <Bar key={c} dataKey={c} name={String(t(`budget.categories.${c}`))} stackId="spend" fill={colours[c]} isAnimationActive={!reducedMotion} />
        ))}
        {summary.isCurrentYear && (
          <Bar
            dataKey="forecast"
            name={String(t('budget.charts.forecastSeries'))}
            stackId="spend"
            fill={base.forecast}
            fillOpacity={0.25}
            stroke={base.forecast}
            strokeDasharray="4 3"
            isAnimationActive={!reducedMotion}
          />
        )}
      </BarChart>
    </ChartFrame>
  );
}

/** Month by month: this year next to the previous year. */
export function YearComparisonChart({ summary, caption }: BudgetChartProps) {
  const { base } = useBudgetColours();
  const { language, tick, money, tooltipMoney } = useAxis();
  const reducedMotion = usePrefersReducedMotion();
  const elapsed = monthsElapsed(summary);
  const data = summary.months.map((m, i) => ({
    label: monthLabel(m.month, language),
    previous: (summary.previousMonths[i]?.totalCents ?? 0) / 100,
    current: m.month <= elapsed ? m.totalCents / 100 : null,
  }));
  return (
    <ChartFrame caption={caption} testId="budget-comparison-chart">
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={base.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" stroke={base.axis} tick={tick} tickLine={false} interval="preserveStartEnd" minTickGap={4} />
        <YAxis stroke={base.axis} tick={tick} tickLine={false} tickFormatter={money} width={64} />
        <Tooltip wrapperClassName="budget-chart__tooltip" formatter={tooltipMoney} isAnimationActive={!reducedMotion} />
        <Legend />
        <Bar dataKey="previous" name={String(summary.year - 1)} fill={base.previous} isAnimationActive={!reducedMotion} />
        <Bar dataKey="current" name={String(summary.year)} fill={base.current} isAnimationActive={!reducedMotion} />
      </BarChart>
    </ChartFrame>
  );
}

/** Running total of the year against the previous year, with the forecast up to 31 December (dashed). */
export function CumulativeForecastChart({ summary, caption }: BudgetChartProps) {
  const { t } = useTranslation();
  const { base } = useBudgetColours();
  const { language, tick, money, tooltipMoney } = useAxis();
  const reducedMotion = usePrefersReducedMotion();
  const data = cumulativeSeries(summary).map((p) => ({ ...p, label: monthLabel(p.month, language) }));
  return (
    <ChartFrame caption={caption} testId="budget-cumulative-chart">
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={base.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" stroke={base.axis} tick={tick} tickLine={false} interval="preserveStartEnd" minTickGap={4} />
        <YAxis stroke={base.axis} tick={tick} tickLine={false} tickFormatter={money} width={64} />
        <Tooltip wrapperClassName="budget-chart__tooltip" formatter={tooltipMoney} isAnimationActive={!reducedMotion} />
        <Legend />
        <Line
          dataKey="previous"
          name={String(summary.year - 1)}
          stroke={base.previous}
          strokeWidth={2}
          strokeDasharray="2 3"
          dot={false}
          isAnimationActive={!reducedMotion}
        />
        <Line dataKey="actual" name={String(summary.year)} stroke={base.current} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={!reducedMotion} />
        {summary.isCurrentYear && (
          <Line
            dataKey="forecast"
            name={String(t('budget.charts.forecastSeries'))}
            stroke={base.forecast}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={!reducedMotion}
          />
        )}
      </LineChart>
    </ChartFrame>
  );
}

/** One line per category over the previous and the selected year (24 months, 3-month average), to show the trend of each. */
export function CategoryTrendChart({ summary, caption, categories }: BudgetChartProps & { categories: readonly BudgetCategory[] }) {
  const { t } = useTranslation();
  const { base, category: colours } = useBudgetColours();
  const { language, tick, money, tooltipMoney } = useAxis();
  const reducedMotion = usePrefersReducedMotion();
  const elapsed = monthsElapsed(summary);
  const data = useMemo(() => {
    const rows: Record<string, number | string | null>[] = [];
    const add = (year: number, months: BudgetSummary['months'], upTo: number) => {
      for (const m of months) {
        const row: Record<string, number | string | null> = { label: `${monthLabel(m.month, language)} ${String(year).slice(2)}` };
        for (const c of categories) row[c] = m.month <= upTo ? m.byCategory[c] / 100 : null;
        rows.push(row);
      }
    };
    add(summary.year - 1, summary.previousMonths, 12);
    add(summary.year, summary.months, elapsed);
    // Purchases come in lumps (a salt bucket every few months): smooth to a 3-month average so the trend shows.
    for (const c of categories) {
      const smoothed = rollingAverage(rows.map((r) => r[c] as number | null));
      rows.forEach((r, i) => {
        r[c] = smoothed[i] ?? null;
      });
    }
    return rows;
  }, [summary, categories, language, elapsed]);
  return (
    <ChartFrame caption={caption} testId="budget-trend-chart">
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={base.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" stroke={base.axis} tick={tick} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
        <YAxis stroke={base.axis} tick={tick} tickLine={false} tickFormatter={money} width={64} />
        <Tooltip wrapperClassName="budget-chart__tooltip" formatter={tooltipMoney} isAnimationActive={!reducedMotion} />
        <Legend />
        {categories.map((c) => (
          <Line
            key={c}
            dataKey={c}
            name={String(t(`budget.categories.${c}`))}
            stroke={colours[c]}
            strokeWidth={2}
            dot={{ r: 2 }}
            isAnimationActive={!reducedMotion}
          />
        ))}
      </LineChart>
    </ChartFrame>
  );
}
